import { z, Schema } from 'zod';
import {
  Channel,
  ChannelSchema,
  DefinitionStatusType,
  DefinitionStatusSchema,
  FieldDefinition,
  FieldDefinitionSchema
} from './schema';
import { ZoneDefinition, ZoneDefinitionSchema } from './zone-definition';

/**
 * Structure of a surface definition.
 * @typedef SurfaceDefinition
 * @property - label - A human readable name of the surface created from this surface definition.
 * @property - description - A human readable description.
 * @property - status - 'active' / 'inactive' state of the surface.
 * @property - channels - An array indicating where surface instances of this type are allowed to appear.
 * @property - fieldDefinitions - An array of field definitions that define the type of data that can be placed on a surface configuration.
 * @property - zoneDefinitions - An optional array of zone definitions that can be used to subdivide the surface for the purposes of placing children modules within the surface configuration.
 */
export type SurfaceDefinition = {
  readonly label: string;
  readonly description: string;
  readonly status: DefinitionStatusType;
  readonly channels: ReadonlyArray<Channel>;
  readonly fieldDefinitions: ReadonlyArray<FieldDefinition>;
  readonly zonesDefinitions?: ReadonlyArray<ZoneDefinition>;
};

export const SurfaceDefinitionSchema: Schema<SurfaceDefinition> = z.object({
  label: z.string(),
  description: z.string(),
  status: DefinitionStatusSchema,
  channels: z.array(ChannelSchema),
  fieldDefinitions: z.array(FieldDefinitionSchema),
  zonesDefinitions: z.array(ZoneDefinitionSchema).optional()
});

/**
 * Indexable version type of a surface defintion.
 * @typedef SurfaceDefinitionVersion
 * @property - version - Format in whole number.
 */
export type SurfaceDefinitionVersion = {
  readonly [version: string]: SurfaceDefinition;
};

export const SurfaceDefinitionVersionSchema: Schema<SurfaceDefinitionVersion> =
  z.record(z.string(), SurfaceDefinitionSchema);

/**
 * Structure of a web surface definition.
 * @typedef WebSurfaceDefinition
 * @property - name - The type of surface instance that will be created from this definition.
 * @property - schema - A semantic version.
 * @property - type - Defines a web surface definition type.
 * @property - versions - Supports surface definition versioning.
 */
export type WebSurfaceDefinition = {
  readonly name: string;
  readonly schema: string;
  readonly type: 'surface';
  readonly versions: SurfaceDefinitionVersion;
};

export const WebSurfaceDefinitionSchema: Schema<WebSurfaceDefinition> =
  z.object({
    name: z.string(),
    schema: z.string(),
    type: z.literal('surface'),
    versions: SurfaceDefinitionVersionSchema
  });

/**
 * Create a versioned surface from a surface definition json.
 * @param  definition  Surface definition json, conforming with SurfaceDefinition type.
 * @returns - Surface definition object.
 * @example
 * ```ts
 *    import { createSurfaceDefinitionVersion } from '@atlassiansox/bxp-definition-sdk';
 *
 *    export const ExampleSurface = createSurfaceDefinitionVersion({
 *      {
 *         label:'Example Surface Definition',
 *         description:'Example Surface Definition',
 *         status:'active',
 *         channels:'web',
 *         fieldDefinitions:[
 *           {
 *             name:'title',
 *             type:'string',
 *             label:'Title'
 *           },
 *           {
 *             name:'description',
 *             type:'string',
 *             label:'Description'
 *           }
 *         ],
 *         zonesDefinitions:[
 *           {
 *             name:'main',
 *             label:'Main Zone',
 *             description:'A zone where an example module can be added.',
 *             status:'active',
 *             validations:{
 *               moduleRestrictions:[ {
 *                   name: 'SomeModule',
 *                   version: '1'
 *               } ]
 *             }
 *           }
 *         ]
 *      }
 *   });
 * ```
 */

export function createSurfaceDefinitionVersion<
  const M extends SurfaceDefinition
>(surfaceDefinition: M): M {
  return SurfaceDefinitionSchema.parse(surfaceDefinition) as M;
}
